import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, X, Filter, Grid, List } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { StatusBadge } from '@/components/common/data-display/StatusBadge'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { EmptyState } from '@/components/common/data-display/EmptyState'
import { Pagination } from '@/components/common/navigation/Pagination'
import { useArtworkSearch, useAdminArtists, useAdminTags } from '../hooks/useAdminSearch'
import { ArtworkSearchParams } from '../api/adminApi'
import { ArtworkTile } from '../components/ArtworkTile'
import { ArtworkStatus } from '@/types/entities'
import { truncate } from '@/lib/utils'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import { PAGINATION } from '@/config/constants'

type ViewMode = 'grid' | 'list'

export function ArtworkSearchPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Initialize from URL query params
  const initialQuery = searchParams.get('q') || ''

  // Search state
  const [searchInput, setSearchInput] = useState(initialQuery)
  const [keyword, setKeyword] = useState(initialQuery)
  const [artistId, setArtistId] = useState<number | undefined>()
  const [status, setStatus] = useState<ArtworkStatus | undefined>()
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGINATION.DEFAULT_PAGE_SIZE)
  const [viewMode, setViewMode] = useState<ViewMode>('grid')

  // Update search when URL params change
  useEffect(() => {
    const q = searchParams.get('q')
    if (q) {
      setSearchInput(q)
      setKeyword(q)
    }
  }, [searchParams])

  // Fetch filter options
  const { data: artists = [] } = useAdminArtists()
  const { data: tags = [] } = useAdminTags()

  // Build search params for filtered results
  const searchParamsObj: ArtworkSearchParams = useMemo(() => ({
    keyword: keyword || undefined,
    artistId,
    status,
    tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
  }), [keyword, artistId, status, selectedTagIds])

  const hasActiveFilters = !!(keyword || artistId || status || selectedTagIds.length > 0)

  // Search artworks (when filters are active)
  const { data: searchResults, isLoading: isSearching } = useArtworkSearch(
    searchParamsObj,
    { start: (page - 1) * pageSize, limit: pageSize },
    hasActiveFilters
  )

  // Recently updated artworks (when no filters)
  const { data: recentArtworks, isLoading: isLoadingRecent } = useArtworkSearch(
    {},
    { start: 0, limit: 24 },
    !hasActiveFilters
  )

  const handleSearch = () => {
    setKeyword(searchInput)
    setPage(1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleClearFilters = () => {
    setSearchInput('')
    setKeyword('')
    setArtistId(undefined)
    setStatus(undefined)
    setSelectedTagIds([])
    setPage(1)
  }

  const handleTagToggle = (tagId: number) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    )
    setPage(1)
  }

  return (
    <div className="space-y-8 p-8">
      {/* Page Header */}
      <div className="space-y-1">
        <h1 className="font-serif text-4xl font-semibold tracking-tight">Artworks</h1>
        <p className="text-muted-foreground text-base">
          Search, browse, and manage all artworks in the platform
        </p>
      </div>

      {/* Search & Filters */}
      <Card className="p-6">
        <div className="space-y-4">
          {/* Keyword Search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, description, or artist..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10"
              />
            </div>
            <Button onClick={handleSearch}>Search</Button>
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Artist Filter */}
            <div className="w-64">
              <Select
                value={artistId?.toString() || 'all'}
                onValueChange={(value) => {
                  setArtistId(value === 'all' ? undefined : Number(value))
                  setPage(1)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Artists" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Artists</SelectItem>
                  {artists.map((artist) => (
                    <SelectItem key={artist.id} value={artist.id.toString()}>
                      {artist.artisticName || `${artist.firstName} ${artist.lastName}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="w-48">
              <Select
                value={status || 'all'}
                onValueChange={(value) => {
                  setStatus(value === 'all' ? undefined : value as ArtworkStatus)
                  setPage(1)
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value={ArtworkStatus.DRAFT}>Draft</SelectItem>
                  <SelectItem value={ArtworkStatus.PENDING}>Pending</SelectItem>
                  <SelectItem value={ArtworkStatus.PUBLISHED}>Published</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex gap-1 ml-auto">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-9 w-9"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-9 w-9"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button variant="ghost" onClick={handleClearFilters}>
                <X className="mr-2 h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>

          {/* Tag Filters */}
          {tags.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Tags</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={selectedTagIds.includes(tag.id) ? 'default' : 'outline'}
                    className="cursor-pointer hover:bg-primary/80"
                    onClick={() => handleTagToggle(tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Results Section */}
      {hasActiveFilters ? (
        /* Search Results */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Search Results</h2>
            {searchResults && (
              <span className="text-sm text-muted-foreground">
                {searchResults.total} artwork{searchResults.total !== 1 ? 's' : ''} found
              </span>
            )}
          </div>

          {isSearching ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : !searchResults || searchResults.data.length === 0 ? (
            <Card className="p-12">
              <EmptyState
                title="No artworks found"
                description="Try adjusting your search criteria or filters"
              />
            </Card>
          ) : viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {searchResults.data.map((artwork) => (
                <ArtworkTile key={artwork.id} artwork={artwork} />
              ))}
            </div>
          ) : (
            /* List View */
            <Card>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-32">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.data.map((artwork) => (
                      <TableRow
                        key={artwork.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/app/artworks/${artwork.id}`)}
                      >
                        <TableCell>
                          <img
                            src={artworkApi.getArtworkImageUrl(artwork.id)}
                            alt={artwork.name}
                            className="w-16 h-16 object-cover rounded"
                            onError={(e) => {
                              // Prevent infinite loop by checking if we're already showing the fallback
                              if (e.currentTarget.src.includes('placeholder-image')) {
                                return
                              }
                              e.currentTarget.src = '/placeholder-image.svg'
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{artwork.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">
                              {artwork.artist?.artisticName ||
                               `${artwork.artist?.firstName} ${artwork.artist?.lastName}`}
                            </div>
                            <div className="text-muted-foreground">
                              {artwork.artist?.email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-md text-muted-foreground">
                          {truncate(artwork.description || '', 80)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={artwork.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}

          {searchResults && searchResults.total > pageSize && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={searchResults.total}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      ) : (
        /* Recently Updated Artworks */
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Recently Updated</h2>

          {isLoadingRecent ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : !recentArtworks || recentArtworks.data.length === 0 ? (
            <Card className="p-12">
              <EmptyState
                title="No artworks yet"
                description="Artworks will appear here once artists upload their work"
              />
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {recentArtworks.data.map((artwork) => (
                <ArtworkTile key={artwork.id} artwork={artwork} />
              ))}
            </div>
          ) : (
            <Card>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-20">Image</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Artist</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-32">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentArtworks.data.map((artwork) => (
                      <TableRow
                        key={artwork.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/app/artworks/${artwork.id}`)}
                      >
                        <TableCell>
                          <img
                            src={artworkApi.getArtworkImageUrl(artwork.id)}
                            alt={artwork.name}
                            className="w-16 h-16 object-cover rounded"
                            onError={(e) => {
                              // Prevent infinite loop by checking if we're already showing the fallback
                              if (e.currentTarget.src.includes('placeholder-image')) {
                                return
                              }
                              e.currentTarget.src = '/placeholder-image.svg'
                            }}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{artwork.name}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">
                              {artwork.artist?.artisticName ||
                               `${artwork.artist?.firstName} ${artwork.artist?.lastName}`}
                            </div>
                            <div className="text-muted-foreground">
                              {artwork.artist?.email}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-md text-muted-foreground">
                          {truncate(artwork.description || '', 80)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={artwork.status} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}
